import { getUnprocessableErrors } from '@10xlabs/utils'
import { useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams, useSearchParams } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { IVehiclesV2FormInsuranceStepValues, IVehiclesV2FormValues } from 'features/Vehicles/types'
import { VehiclesV2FormUtils } from 'features/Vehicles/utils'
import {
  CHANGE_VEHICLE_INSURANCE_POLICY,
  CHANGE_VEHICLE_REGISTRATION_DETAILS
} from 'features/Vehicles/mutations-legacy'
import { getDifference } from 'utils/object'
import { schema } from './schema'
import { InsurancePolicyForm } from 'features/Vehicles/components/InsurancePolicyForm'
import { usePushFileToS3 } from 'utils/file'
import { VehicleRegistrationForm } from 'features/Vehicles/components/VehicleRegistrationForm'

export const NewVehiclesV2InsuranceStep = ({
  vehicle,
  onSubmit,
  onCancel
}: {
  vehicle?: IVehiclesV2FormValues
  onSubmit: () => void
  onCancel: () => void
}): ReactElement => {
  const intl = useIntl()
  const pushFileToS3 = usePushFileToS3()
  const [searchParams] = useSearchParams()
  const params = useParams()
  const vehicleId = params.vehicleId || searchParams.get('vehicleId')

  const [changeVehicleRegistrationDetails, { error: changeVehicleRegistrationDetailsError }] = useMutation(
    CHANGE_VEHICLE_REGISTRATION_DETAILS
  )
  const [changeInsurancePolicy, { error: changeInsurancePolicyError }] = useMutation(CHANGE_VEHICLE_INSURANCE_POLICY)

  const handleSubmit = async (
    values: IVehiclesV2FormInsuranceStepValues,
    { setSubmitting }: FormikHelpers<IVehiclesV2FormInsuranceStepValues>
  ): Promise<void> => {
    try {
      const oldVehicleRegistration = VehiclesV2FormUtils.mapVehicleRegistrationFormToMutation(
        vehicle as IVehiclesV2FormInsuranceStepValues
      )
      if (values.registration.document?.file) {
        const filePath = await pushFileToS3(values.registration.document.file)
        values.registration.document.path = filePath
      } else if (values.registration.document && oldVehicleRegistration) {
        oldVehicleRegistration.filePath = undefined
        values.registration.document.path = undefined
      }
      const newVehicleRegistration = VehiclesV2FormUtils.mapVehicleRegistrationFormToMutation(values)

      if (newVehicleRegistration !== null && !isEmpty(getDifference(newVehicleRegistration, oldVehicleRegistration))) {
        await changeVehicleRegistrationDetails({
          variables: {
            id: vehicleId,
            input: newVehicleRegistration
          }
        })
      }
      const oldInsurancePolicy = VehiclesV2FormUtils.mapVehicleInsurancePolicyFormToMutation(
        vehicle as IVehiclesV2FormInsuranceStepValues
      )
      if (values.insurancePolicy.document.file) {
        const filePath = await pushFileToS3(values.insurancePolicy.document.file)
        values.insurancePolicy.document.path = filePath
      } else {
        oldInsurancePolicy.filePath = undefined
        values.insurancePolicy.document.path = undefined
      }
      const newInsurancePolicy = VehiclesV2FormUtils.mapVehicleInsurancePolicyFormToMutation(values)

      if (!isEmpty(getDifference(newInsurancePolicy, oldInsurancePolicy))) {
        await changeInsurancePolicy({
          variables: {
            id: vehicleId,
            input: newInsurancePolicy
          }
        })
      }
      onSubmit()
    } catch (ex) {
      // DO NOTHING
    }
    setSubmitting(false)
  }

  const initialValues: IVehiclesV2FormInsuranceStepValues = {
    insurancePolicy: {
      expirationDate: vehicle?.insurancePolicy?.expirationDate || '',
      document: vehicle?.insurancePolicy?.document || {
        name: '',
        file: '',
        path: ''
      }
    },
    registration: {
      isPermanent: vehicle?.registration?.isPermanent || false,
      expirationDate: vehicle?.registration?.expirationDate || '',
      state: vehicle?.registration?.state || null,
      category: vehicle?.registration?.category || null,
      document: vehicle?.registration?.document || {
        name: '',
        file: '',
        path: ''
      }
    }
  }

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={schema(intl)}>
      {({ handleSubmit, isSubmitting }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <FormLayout.Card small>
              <VehicleRegistrationForm />
            </FormLayout.Card>
            <FormLayout.Card small>
              <InsurancePolicyForm />
            </FormLayout.Card>
            {changeInsurancePolicyError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeInsurancePolicyError)}>
                  <FormattedMessage
                    id="vehicles.form.insurancePolicy.error"
                    defaultMessage="There was an error while saving the insurance policy"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changeVehicleRegistrationDetailsError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeVehicleRegistrationDetailsError)}>
                  <FormattedMessage
                    id="vehicles.form.registration.error"
                    defaultMessage="There was an error while saving the registration details"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" loading={isSubmitting}>
                <FormattedMessage id="vehicles.form.insuranceStep.save" defaultMessage="Next" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
